import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { useTranslation } from "../i18n";
import { SocialSignIn } from "../components/SocialSignIn";
import { AuthShell, CodeInput, LegalNotice, PasswordInput } from "../components/AuthShell";
import { Alert, Button, Field, Input, cx } from "../components/ui";
import { ApiError } from "../lib/api";
import { CheckYourEmail } from "./CheckYourEmail";
import { countdown } from "../lib/format";
import { verifyFailure } from "../lib/twoFactor";

// Reissued challenges currently omit their TTL. Initial challenges always use
// the server-provided value; this only avoids keeping a spent token forever.
const FALLBACK_CHALLENGE_TTL_MS = 5 * 60 * 1000;

function destinationFrom(state: unknown): string {
  const from =
    state && typeof state === "object" && "from" in state && typeof state.from === "string"
      ? state.from
      : null;
  return from?.startsWith("/") && !from.startsWith("//") ? from : "/apps";
}

function challengeFrom(state: unknown): { challengeToken: string; expiresIn: number } | null {
  if (!state || typeof state !== "object" || !("oauthChallenge" in state)) return null;
  const challenge = (state as { oauthChallenge: unknown }).oauthChallenge;
  if (!challenge || typeof challenge !== "object") return null;
  const { challengeToken, expiresIn } = challenge as { challengeToken?: unknown; expiresIn?: unknown };
  return typeof challengeToken === "string" && typeof expiresIn === "number" ? { challengeToken, expiresIn } : null;
}

export function LoginPage() {
  const { status, login, verifyTwoFactor } = useAuth();
  const { t } = useTranslation("auth");
  const location = useLocation();
  const navigate = useNavigate();
  const destination = destinationFrom(location.state);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  /*
   * A social sign-in that met a second factor lands here with the challenge
   * in the router state, so this page runs the step it already knows rather
   * than a second copy of it on the landing page.
   */
  const handed = challengeFrom(location.state);
  const [challengeToken, setChallengeToken] = useState<string | null>(handed?.challengeToken ?? null);
  const [challengeExpiry, setChallengeExpiry] = useState<number | null>(
    handed ? Date.now() + handed.expiresIn * 1000 : null,
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [lockedOut, setLockedOut] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  /*
   * Set when the password was right and the address has never been confirmed.
   * The whole screen becomes the confirmation panel, because there is nothing
   * useful to do on the login form until the address answers — and the panel
   * carries the resend, which is what this person actually needs.
   */
  const [unverified, setUnverified] = useState<string | null>(null);

  // Challenge expiry countdown
  useEffect(() => {
    if (!challengeExpiry || lockedOut) {
      if (!challengeExpiry) setTimeRemaining(null);
      return;
    }

    const update = () => {
      const remaining = Math.max(0, challengeExpiry - Date.now());
      setTimeRemaining(remaining);
      if (remaining === 0) abandonChallenge(t("verify.expired"));
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [challengeExpiry, lockedOut]);

  if (status === "authenticated") return <Navigate to={destination} replace />;

  /*
   * `emailSent` is deliberately not passed: this path has no idea whether a
   * message went out, and the panel's default of "we sent one" is the right
   * thing to say to somebody who registered at some point in the past. The
   * resend is right there either way.
   */
  if (unverified) return <CheckYourEmail email={unverified} />;

  /*
   * Drop the challenge and go back to the password form. Written once because it
   * was three near-copies — the lockout branch, the "back to login" link and the
   * expired-challenge path — which had drifted into clearing different subsets of
   * the state between them.
   *
   * It clears the local lockout notice too. The real limit is server-side and
   * sliding, so the interface deliberately avoids promising a false countdown.
   */
  function abandonChallenge(message: string | null = null) {
    setChallengeToken(null);
    setChallengeExpiry(null);
    setLockedOut(false);
    setPassword("");
    setCode("");
    setUseRecoveryCode(false);
    setError(message);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const outcome = await login(email, password);
      if (outcome.kind === "challenge") {
        setChallengeToken(outcome.challengeToken);
        setChallengeExpiry(Date.now() + outcome.expiresIn * 1000);
        setLockedOut(false);
        setCode("");
        setUseRecoveryCode(false);
      } else {
        void navigate(destination, { replace: true });
      }
    } catch (cause) {
      /*
       * Matched on the code, never on the message. The API says as much in the
       * comment on `email_unverified`: branching on prose is a branch that stops
       * working the next time somebody edits the copy, silently, leaving this
       * screen showing "confirm your email" as a dead-end error with no resend.
       */
      if (cause instanceof ApiError && cause.code === "email_unverified") {
        setUnverified(email);
        return;
      }
      setError(cause instanceof ApiError ? cause.message : t("login.failed"));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleVerify(event: FormEvent) {
    event.preventDefault();
    if (!challengeToken || lockedOut) return;
    setError(null);
    setSubmitting(true);
    try {
      await verifyTwoFactor(challengeToken, code);
      void navigate(destination, { replace: true });
    } catch (cause) {
      // A transport failure does not spend a server challenge. Keep the password
      // proof and let the customer retry rather than making them start over.
      if (!(cause instanceof ApiError) || cause.status >= 500) {
        setError(t("verify.network"));
        return;
      }

      const failure = verifyFailure(cause);
      if (failure.restart) {
        // The server does not distinguish a lockout in `code`, so the message is
        // all there is to go on. Worth replacing with a real code on the API side.
        const isLockout = cause.message.includes("Too many attempts");
        if (isLockout) {
          setLockedOut(true);
          setCode("");
          setError(cause.message);
        } else {
          abandonChallenge(cause.message);
        }
      } else {
        setChallengeToken(failure.nextChallengeToken ?? challengeToken);
        setChallengeExpiry(Date.now() + FALLBACK_CHALLENGE_TTL_MS);
        setCode("");
        setError(t("verify.incorrect", { count: 6 - failure.attempts }));
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (challengeToken) {
    const isLockedOut = lockedOut;
    // Under a minute left, the countdown stops being neutral information.
    const expiringSoon = timeRemaining !== null && timeRemaining > 0 && timeRemaining < 60_000;

    return (
      <AuthShell
        title={t("verify.title")}
        subtitle={t("verify.subtitle")}
        footer={
          <button
            type="button"
            onClick={() => abandonChallenge()}
            className="text-sm text-fg-subtle transition-colors duration-200 ease-smooth hover:text-fg"
          >
            {t("verify.back")}
          </button>
        }
      >
        <div className="space-y-5">
          <div className="border-b border-line pb-4 text-center">
            <p className="micro text-accent">{t("verify.account")}</p>
            <p className="mt-2 break-all text-sm font-medium text-fg" title={email}>
              {email}
            </p>
          </div>

          {isLockedOut ? (
            <Alert>
              {error ?? t("verify.lockedOut")}
            </Alert>
          ) : (
            error && <Alert>{error}</Alert>
          )}

          {!isLockedOut && (
            <form onSubmit={handleVerify} className="space-y-4">
              <CodeInput
                id="code"
                label={useRecoveryCode ? t("verify.recoveryCode") : t("verify.authCode")}
                value={code}
                onChange={setCode}
                numeric={!useRecoveryCode}
                disabled={submitting}
                autoFocus
              />

              <Button
                type="submit"
                size="xl"
                loading={submitting}
                disabled={!code.trim()}
                className="w-full"
              >
                {t("verify.submit")}
              </Button>

              <div className="flex flex-col-reverse gap-3 border-t border-line pt-4 text-[13px] sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={() => {
                    setUseRecoveryCode((current) => !current);
                    setCode("");
                  }}
                  className="text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
                >
                  {useRecoveryCode ? t("verify.useAuthenticator") : t("verify.useRecovery")}
                </button>

                {timeRemaining !== null && timeRemaining > 0 && (
                  <span
                    className={cx(
                      "shrink-0 tabular-nums",
                      expiringSoon ? "text-warn" : "text-fg-subtle",
                    )}
                  >
                    {t("verify.expiresIn", { time: countdown(timeRemaining) })}
                  </span>
                )}
              </div>
            </form>
          )}
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={t("login.title")}
      /* "Pick up where you left off" was warm and said nothing. This names what
         is behind the door, in the platform's own three parts — and says "the
         models they call" rather than "your models", because the weights are
         bought and a possessive is the cheapest way to imply otherwise. */
      subtitle={t("login.subtitle")}
      footer={
        <>
          {t("login.needAccount")}{" "}
          <Link
            className="text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
            /*
             * The query string travels with the link.
             *
             * Without it a visitor who lands on `/register?ref=ana`, clicks
             * through to sign in, then comes back to create an account arrives
             * with no code — and attribution falls back to the marketing site's
             * cookie, which only exists where the visitor accepted functional
             * storage. An affiliate who linked straight to the dashboard has no
             * cookie at all, so that round trip lost the commission outright.
             *
             * The whole search rather than just `ref`: `promo` rides here too
             * and has the identical problem.
             */
            to={{ pathname: "/register", search: location.search }}
            state={location.state}
          >
            {t("login.createOne")}
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && <Alert>{error}</Alert>}

        <Field label={t("login.email")} htmlFor="email">
          <Input
            id="email"
            size="xl"
            surface="canvas"
            type="email"
            autoComplete="username"
            required
            autoFocus
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        {/* The reset link rides the label row rather than sitting under the
            field. Below it, it was one more thing between the last input and
            the submit button — on the path of somebody who is signing in
            normally, which is almost everybody. */}
        <Field
          label={t("login.password")}
          htmlFor="password"
          action={
            <Link
              to="/reset-password"
              state={location.state}
              className="text-[13px] text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
            >
              {t("login.forgot")}
            </Link>
          }
        >
          <PasswordInput
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>

        <Button type="submit" size="xl" loading={submitting} className="w-full">
          {t("login.submit")}
        </Button>

        <SocialSignIn returnTo={destination} mode="signin" />

        <LegalNotice action="signingIn" />
      </form>
    </AuthShell>
  );
}
